import { useState } from "react";
import { Check, X, ExternalLink } from "lucide-react";
import { decideNaicsSuggestions, type NaicsSuggestion, type PrimeCompany } from "../api/CrmApi";
import StatusPill from "shared/StatusPill";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { formatDate } from "./crmUi";

const CONFIDENCE_TONE = { high: "success", medium: "warning", low: "neutral" } as const;
const SOURCE_LABEL = { service_inference: "from its services", web_research: "from web research" } as const;

interface Props {
  company: PrimeCompany;
  onChanged: (company: PrimeCompany) => void;
}

/** NAICS codes proposed for a company with none. A pending suggestion is NOT used by any RFP check; approving one
 *  makes it a normal company code — including for excluding the company from RFPs outside it — so read the evidence. */
export default function NaicsSuggestionsBox({ company, onChanged }: Props) {
  const all = company.naics_suggestions ?? [];
  const pending = all.filter((s) => s.status === "pending");
  const decided = all.filter((s) => s.status !== "pending");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecided, setShowDecided] = useState(false);

  async function decide(decisions: { code: string; decision: "approve" | "reject" }[]) {
    setBusy(true);
    setError(null);
    try {
      onChanged(await decideNaicsSuggestions(company.id, decisions));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not save the decision — reload and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (all.length === 0 && !company.naics_researched_at) return null;

  const high = pending.filter((s) => s.confidence === "high");

  return (
    <div className="space-y-2" data-testid="naics-suggestions">
      <p className="text-xs font-medium text-muted-foreground">NAICS suggestions</p>
      {pending.length > 0 ? (
        <div className="rounded-xl border border-warning-border bg-warning-soft/40 px-3 py-3 space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Proposed by research, not active yet: no RFP check counts these until you approve them. An approved code also excludes
            the company from RFPs outside it, so check the evidence.
          </p>
          {pending.map((s) => (
            <SuggestionRow key={s.code} s={s} busy={busy} onDecide={(d) => decide([{ code: s.code, decision: d }])} />
          ))}
          {high.length > 1 && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => decide(high.map((s) => ({ code: s.code, decision: "approve" as const })))}>
              <Check className="w-3.5 h-3.5" /> Approve all {high.length} high-confidence
            </Button>
          )}
        </div>
      ) : (
        all.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Researched {company.naics_researched_at ? formatDate(company.naics_researched_at, true) : ""} — no NAICS code could be suggested. Add one by hand below.
          </p>
        )
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      {decided.length > 0 && (
        <div>
          <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setShowDecided((v) => !v)}>
            {showDecided ? "Hide" : "Show"} {decided.length} earlier decision{decided.length === 1 ? "" : "s"}
          </button>
          {showDecided && (
            <ul className="mt-1 space-y-1">
              {decided.map((s) => (
                <li key={s.code} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="font-mono">{s.code}</span> {s.title}
                  <StatusPill tone={s.status === "approved" ? "success" : "neutral"} size="sm">{s.status}{s.decided_by === "manual" ? " (typed)" : ""}</StatusPill>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ s, busy, onDecide }: { s: NaicsSuggestion; busy: boolean; onDecide: (d: "approve" | "reject") => void }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="font-mono text-sm font-semibold text-foreground">{s.code}</span>
        <span className="text-sm text-foreground flex-1 min-w-0">{s.title ?? "(unknown code)"}</span>
        <StatusPill tone={CONFIDENCE_TONE[s.confidence]} size="sm" title="How sure the research was">{s.confidence} confidence</StatusPill>
        <span className="text-[11px] text-muted-foreground">{SOURCE_LABEL[s.source]}</span>
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {s.evidence.map((e, i) => (
          <li key={i} className="text-xs text-muted-foreground break-words">
            {e.kind === "service_category" ? "Service: " : "Web: "}
            {e.text}
            {e.url && (
              <a href={e.url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-primary hover:underline">
                source <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex gap-2">
        <Button variant="primary" size="sm" disabled={busy} onClick={() => onDecide("approve")} aria-label={`Approve ${s.code}`}>
          <Check className="w-3.5 h-3.5" /> Approve
        </Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => onDecide("reject")} aria-label={`Reject ${s.code}`}>
          <X className="w-3.5 h-3.5" /> Reject
        </Button>
      </div>
    </div>
  );
}
